import React, { useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useProviderSignin } from '../hooks/provider'
import { useLoad } from '../../../../shared/hooks/load'
import { useQueryParam } from '../../../../shared/hooks/location'
import { useApiListAuthProviderLink, useApiListProviderDomainStatus } from '../../../../api-new/state/auth'
import { authPath, carryParams, domainOf, providerLinkKey } from '../hooks/auth-path'
import { fetchDomainStatus, passwordAllowedBy } from '../hooks/domain-status'
import { ProviderLogo } from '../../../../shared/sso-workspace/provider-logo'
import { AuthDivider, AuthHandoff, AuthHead, AuthLoading, AuthNotice, AuthOption, AuthPage, AuthWho } from '../../../../shared/sso-workspace/auth-card'

// /auth/providers: which connection to sign in with. One connection signs
// straight in; none sends the person back to the email step; two or more
// show a card per connection (its logo and name).
//
// Every way out keeps the email and where to go after signing in (redirect,
// redirect-doc): the chosen connection's sign-in, "Change", and "Use my
// password instead".
//
// Require SSO (owner 2026-10-06): before the choices show, the email's
// domain status is asked whether the server takes a password there; when it
// says passwordAllowed: false, "Use my password instead" is not offered. A
// failed lookup offers it as before. One connection signs straight in and
// never asks (the lookup is rate-limited), unless starting it fails and the
// choice comes back.
export const LoginProviders = () => {
  const history = useHistory()

  const apiListAuthProviderLink = useApiListAuthProviderLink()

  // api-new hooks return a new object on every request-state change; read
  // this one through a ref so the async paths below always see the current.
  const apiListProviderDomainStatus = useApiListProviderDomainStatus()
  const statusRef = useRef(apiListProviderDomainStatus)
  statusRef.current = apiListProviderDomainStatus

  const providerSignin = useProviderSignin()

  // A sign-in started from an invite page carries the invite key in router
  // state, never the URL (owner direction 2026-09-27).
  const location = useLocation()
  const inviteKey = (location.state && location.state.inviteKey) || ''

  let email = useQueryParam('email')
  let redirect = useQueryParam('redirect')
  let redirectDoc = useQueryParam('redirect-doc')

  // loading -> choose (two or more), or redirecting (one, or once chosen).
  // passwordAllowed is null until the domain status has been asked.
  const [state, updateState] = useState({ step: 'loading', connections: [], chosen: null, failed: false, passwordAllowed: null })

  const askPasswordAllowed = async () => passwordAllowedBy(await fetchDomainStatus(statusRef.current, domainOf(email)))

  const emailPath = authPath('/auth/email', carryParams({ redirect, redirectDoc, email }))
  const passwordPath = authPath('/auth/email', { ...carryParams({ redirect, redirectDoc, email }), use: 'password' })

  // Starting a sign-in can fail before the browser leaves (the passive
  // token request): come back to the choices, or to a retry, with a note.
  const signIn = async (providerLink, connections) => {
    updateState(prev => ({ step: 'redirecting', connections: connections || prev.connections, chosen: providerLink, failed: false }))

    try {
      if (inviteKey) {
        await providerSignin(providerLink, email, redirect, redirectDoc, inviteKey)
      } else {
        await providerSignin(providerLink, email, redirect, redirectDoc)
      }
    } catch {
      // The choice comes back, so it needs the password answer: asked here
      // when the one connection signed straight in without it.
      const passwordAllowed = state.passwordAllowed ?? (await askPasswordAllowed())
      updateState(prev => ({ ...prev, step: 'choose', failed: true, passwordAllowed: prev.passwordAllowed ?? passwordAllowed }))
    }
  }

  useLoad(
    async () => {
      if (!email || !domainOf(email)) {
        history.replace(emailPath)
        return
      }

      let connections = []

      try {
        // The full email, so a connection limited to named testers can
        // match (audit B2).
        connections = (await apiListAuthProviderLink.get(providerLinkKey(email))) || []
      } catch {}

      if (connections.length === 0) {
        history.replace(emailPath)
      } else if (connections.length === 1) {
        signIn(connections[0], connections)
      } else {
        const passwordAllowed = await askPasswordAllowed()
        updateState({ step: 'choose', connections, chosen: null, failed: false, passwordAllowed })
      }
    },
    [email]
  )

  const choosing = state.step === 'choose'
  const several = state.connections.length > 1

  return (
    <AuthPage labelledBy="providers-title">
      <AuthHead
        id="providers-title"
        title={choosing && several ? 'Choose how to sign in' : 'Sign in'}
        lede={choosing && several
          ? 'Your email can sign in more than one way. Choose the one your organization asked you to use.'
          : state.step === 'redirecting' ? "You'll finish signing in on your organization's page." : undefined}
      />

      <AuthWho email={email} changeTo={emailPath} />

      {state.step === 'loading' && <AuthLoading>Finding how you sign in…</AuthLoading>}

      {state.step === 'redirecting' && state.chosen &&
        <AuthHandoff providerLink={state.chosen}>Taking you to {state.chosen.displayName || 'your sign-in'}…</AuthHandoff>}

      {state.failed &&
        <AuthNotice tone="bad" role="alert">
          <p>We couldn't start signing you in with {(state.chosen && state.chosen.displayName) || 'that connection'}. Check your connection and try again.</p>
        </AuthNotice>}

      {choosing &&
        <ul className="sa-options" aria-labelledby="providers-title">
          {state.connections.map((providerLink, i) =>
            <li key={providerLink.identifier || i}>
              <AuthOption
                logo={<ProviderLogo iconSVG={providerLink.iconSVG} iconSVGDark={providerLink.iconSVGDark} />}
                name={providerLink.displayName}
                onClick={() => signIn(providerLink)}
              />
            </li>
          )}
        </ul>}

      {choosing && state.passwordAllowed &&
        <React.Fragment>
          <AuthDivider />
          <Link className="btn sw-btn sa-block" to={passwordPath}>Use my password instead</Link>
        </React.Fragment>}
    </AuthPage>
  )
}
